// src/components/PatientRecords.tsx
//
// Sección Pacientes, rediseñada sobre la entidad real (migración 023).
//
// Hasta ahora los "pacientes" se derivaban agrupando turnos y no se podían
// editar (pedido de Adolfo Gallo). Ahora hay dos solapas:
//
//   Fichas     → pacientes de verdad, editables, con sus turnos, historias
//                clínicas y recetas contados.
//   Sin ficha  → contactos que solo sacaron turno. Un botón los convierte.
//
// La conversión pide confirmar el corte de nombre y apellido, porque Qbi2 los
// exige separados y ningún algoritmo acierta siempre con apellidos compuestos.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, UserPlus, Search, Pencil, IdCard, Phone, Mail, ShieldCheck,
  CalendarDays, ClipboardList, FileSignature, Loader2, X, Check, ArrowRight, Inbox,
  Sparkles, AlertCircle, FileText, ArrowLeft,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface Paciente {
  id: number;
  apellido: string; nombre: string;
  tipo_doc: string; nro_doc: string | null;
  sexo: string | null; fecha_nacimiento: string | null;
  telefono: string | null; email: string | null;
  obra_social_nombre: string | null; nro_afiliado: string | null;
  turnos: string; historias: string; recetas: string;
  ultimo_turno: string | null;
}

interface Contacto {
  client_name: string; client_phone: string | null; client_email: string | null;
  turnos: number; ultimo_turno: string | null; turno_id: number;
  extra: Record<string, any> | null;
}

type Solapa = 'fichas' | 'sinficha';

const PatientRecords: React.FC = () => {
  const navigate = useNavigate();
  const [solapa, setSolapa] = useState<Solapa>('fichas');
  const [busqueda, setBusqueda] = useState('');
  const [cargando, setCargando] = useState(true);
  const [pacientes, setPacientes] = useState<Paciente[]>([]);
  const [contactos, setContactos] = useState<Contacto[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [editando, setEditando] = useState<Paciente | null>(null);
  const [viendo, setViendo] = useState<Paciente | null>(null);
  const [convirtiendo, setConvirtiendo] = useState<Contacto | null>(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const profId = user?.id;
  const auth = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const base = `${API_URL}/api/patient-records/professional/${profId}`;

  const cargar = useCallback(async () => {
    if (!profId) return;
    setCargando(true);
    setError(null);
    try {
      const q = busqueda.trim().length >= 2 ? `?q=${encodeURIComponent(busqueda.trim())}` : '';
      const [rf, rc] = await Promise.all([
        fetch(`${base}${q}`, { headers: auth }),
        fetch(`${base}/unconverted${q}`, { headers: auth }),
      ]);
      const jf = await rf.json();
      const jc = await rc.json();
      if (jf.success) setPacientes(jf.patients || []);
      if (jc.success) setContactos(jc.contactos || []);
      if (!jf.success && !jc.success) setError('No pudimos cargar los pacientes');
    } catch {
      setError('Error de conexión');
    } finally {
      setCargando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profId, busqueda]);

  useEffect(() => {
    const t = setTimeout(cargar, busqueda ? 350 : 0); // debounce al tipear
    return () => clearTimeout(t);
  }, [cargar, busqueda]);

  const fecha = (iso?: string | null) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return null; }
  };

  const iniciales = (a: string, n: string) =>
    `${(a || '?')[0]}${(n || '')[0] || ''}`.toUpperCase();

  return (
    <div className="space-y-5">
      {/* Volver, arriba del título y discreto, como en Recetas. */}
      <button
        onClick={() => navigate('/dashboard/profesional')}
        className="flex items-center gap-1 text-[#0B7C87] text-[15px] -ml-1 active:opacity-60 transition-opacity"
      >
        <ArrowLeft className="w-5 h-5" />
        Volver
      </button>

      {/* Encabezado */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pacientes</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Sus datos, turnos, historias clínicas y recetas en un solo lugar
          </p>
        </div>
        <button
          onClick={() => setEditando({ id: 0 } as Paciente)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 text-white text-sm font-semibold shadow-sm active:scale-95 transition-transform"
        >
          <UserPlus className="w-4 h-4" />
          Nuevo paciente
        </button>
      </div>

      {/* Buscador */}
      <div className="relative">
        <Search className="w-4 h-4 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" />
        <input
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar por nombre, DNI, teléfono o email…"
          className="w-full pl-11 pr-4 py-3 rounded-2xl border border-gray-200 bg-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
        />
      </div>

      {/* Solapas */}
      <div className="flex gap-2">
        {([
          ['fichas', 'Fichas', pacientes.length, <Users className="w-4 h-4" key="a" />],
          ['sinficha', 'Sin ficha', contactos.length, <Inbox className="w-4 h-4" key="b" />],
        ] as const).map(([id, label, n, icono]) => (
          <button
            key={id}
            onClick={() => setSolapa(id as Solapa)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
              solapa === id
                ? 'bg-gray-900 text-white'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {icono}
            {label}
            <span className={`text-xs px-1.5 py-0.5 rounded-md ${
              solapa === id ? 'bg-white/20' : 'bg-gray-100 text-gray-500'
            }`}>{n}</span>
          </button>
        ))}
      </div>

      {cargando && (
        <div className="py-16 flex flex-col items-center">
          <Loader2 className="w-7 h-7 text-indigo-500 animate-spin" />
          <p className="mt-3 text-sm text-gray-500">Cargando…</p>
        </div>
      )}

      {!cargando && error && (
        <div className="py-10 text-center text-sm text-red-600">{error}</div>
      )}

      {/* ---------- Fichas ---------- */}
      {!cargando && !error && solapa === 'fichas' && (
        pacientes.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto">
              <Users className="w-7 h-7 text-gray-400" />
            </div>
            <p className="mt-4 text-sm font-medium text-gray-700">Todavía no tenés fichas</p>
            <p className="mt-1 text-sm text-gray-500">
              Convertí un contacto desde la solapa <strong>Sin ficha</strong> o creá uno nuevo.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {pacientes.map((p) => (
              <div
                key={p.id}
                onClick={() => setViendo(p)}
                className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              >
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white flex items-center justify-center font-semibold text-sm flex-shrink-0">
                    {iniciales(p.apellido, p.nombre)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-gray-900 leading-tight truncate">
                      {p.apellido}, {p.nombre}
                    </p>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                      {p.nro_doc && (
                        <span className="inline-flex items-center gap-1">
                          <IdCard className="w-3 h-3" />{p.tipo_doc} {p.nro_doc}
                        </span>
                      )}
                      {p.telefono && (
                        <span className="inline-flex items-center gap-1">
                          <Phone className="w-3 h-3" />{p.telefono}
                        </span>
                      )}
                      {p.email && (
                        <span className="inline-flex items-center gap-1 truncate">
                          <Mail className="w-3 h-3" />{p.email}
                        </span>
                      )}
                    </div>
                    {p.obra_social_nombre && (
                      <span className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-teal-50 border border-teal-200 text-teal-700 text-[11px] font-medium">
                        <ShieldCheck className="w-3 h-3" />
                        {p.obra_social_nombre}{p.nro_afiliado ? ` · ${p.nro_afiliado}` : ''}
                      </span>
                    )}
                  </div>
                  {/* stopPropagation: la tarjeta entera abre el historial, y
                      sin esto editar haría las dos cosas a la vez. */}
                  <button
                    onClick={(e) => { e.stopPropagation(); setEditando(p); }}
                    className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center flex-shrink-0 transition-colors"
                    title="Editar datos"
                  >
                    <Pencil className="w-3.5 h-3.5 text-gray-600" />
                  </button>
                </div>

                {/* Lo que tiene asociado */}
                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-4 text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />{p.turnos} turnos</span>
                  <span className="inline-flex items-center gap-1"><ClipboardList className="w-3.5 h-3.5" />{p.historias} HC</span>
                  <span className="inline-flex items-center gap-1"><FileSignature className="w-3.5 h-3.5" />{p.recetas} recetas</span>
                  {fecha(p.ultimo_turno) && <span className="ml-auto">Últ. {fecha(p.ultimo_turno)}</span>}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* ---------- Sin ficha ---------- */}
      {!cargando && !error && solapa === 'sinficha' && (
        contactos.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto">
              <Check className="w-7 h-7 text-emerald-600" />
            </div>
            <p className="mt-4 text-sm font-medium text-gray-700">No queda nadie sin ficha</p>
            <p className="mt-1 text-sm text-gray-500">Todos tus contactos ya son pacientes.</p>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-gray-500 px-1">
              Personas que sacaron turno pero todavía no tienen ficha. Al convertirlas se
              vinculan todos sus turnos.
            </p>
            {contactos.map((c) => (
              <div key={c.turno_id} className="bg-white rounded-2xl border border-gray-200 p-4 flex items-center gap-3 shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-gray-100 text-gray-500 flex items-center justify-center font-semibold text-sm flex-shrink-0">
                  {(c.client_name || '?')[0].toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900 truncate">{c.client_name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {c.turnos} turno{c.turnos === 1 ? '' : 's'}
                    {c.client_phone ? ` · ${c.client_phone}` : ''}
                    {fecha(c.ultimo_turno) ? ` · últ. ${fecha(c.ultimo_turno)}` : ''}
                    {c.extra?.dni ? ` · DNI ${c.extra.dni}` : ''}
                  </p>
                </div>
                <button
                  onClick={() => setConvirtiendo(c)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-indigo-50 text-indigo-700 text-xs font-semibold hover:bg-indigo-100 transition-colors flex-shrink-0"
                >
                  Convertir <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )
      )}

      {viendo && (
        <HistorialPaciente
          paciente={viendo}
          base={base}
          auth={auth}
          onClose={() => setViendo(null)}
          onEditar={() => { setEditando(viendo); setViendo(null); }}
        />
      )}

      {editando && (
        <FichaModal
          paciente={editando.id ? editando : null}
          base={base}
          auth={auth}
          onClose={() => setEditando(null)}
          onGuardado={() => { setEditando(null); cargar(); }}
        />
      )}

      {convirtiendo && (
        <ConversionModal
          contacto={convirtiendo}
          base={base}
          auth={auth}
          onClose={() => setConvirtiendo(null)}
          onConvertido={() => { setConvirtiendo(null); setSolapa('fichas'); cargar(); }}
        />
      )}
    </div>
  );
};

export default PatientRecords;


// =====================================================================
// Buscar la obra social en el padrón PUCO a partir del DNI
//
// PUCO (Superintendencia de Servicios de Salud) sabe qué obra social tiene
// cada persona. Alcanza con el DNI.
//
// Ojo con las expectativas: el padrón devuelve el nombre de la obra social y
// su RNOS, nada más. El número de afiliado NO figura ahí — eso sale del
// carnet y se carga a mano.
//
// Falla en blando: si el padrón no responde, se carga todo a mano y listo.
// =====================================================================
const BotonCobertura: React.FC<{
  form: any; setForm: (f: any) => void; auth: any;
}> = ({ form, setForm, auth }) => {
  const [buscando, setBuscando] = useState(false);
  const [msg, setMsg] = useState<{ tipo: 'ok' | 'info'; texto: string } | null>(null);

  const dniOk = String(form.nro_doc || '').replace(/\D/g, '').length >= 6;

  const buscar = async () => {
    setBuscando(true); setMsg(null);
    try {
      const r = await fetch(
        `${API_URL}/api/msal/coberturas?dni=${encodeURIComponent(form.nro_doc)}`,
        { headers: auth }
      );
      const j = await r.json();
      const c = (j.coberturas || [])[0];
      if (j.encontrado && c) {
        setForm({
          ...form,
          obra_social_nombre: c.obraSocial || form.obra_social_nombre,
          // El RNOS va en su propio campo. Antes se guardaba en
          // `obra_social_id`, que es del catálogo de Qbi2 y usa otra
          // numeración: IOSCOR es 905001 en el padrón y 217 en Qbi2. El
          // asistente de recetas leía ese número como si fuera de Qbi2 y le
          // mandaba uno inexistente, con lo cual la cobertura salía mal sin
          // avisar. `obra_social_id` ahora lo escribe sólo el asistente,
          // cuando el profesional elige la obra social de la lista.
          rnos: c.rnos || c.codigoObraSocial || form.rnos,
          plan_nombre: c.plan || form.plan_nombre,
        });
        setMsg({
          tipo: 'ok',
          texto: `${c.obraSocial}${c.plan ? ` · ${c.plan}` : ''}. El número de afiliado no figura en el padrón, cargalo del carnet.`,
        });
      } else {
        setMsg({ tipo: 'info', texto: j.aviso || 'No figura en el padrón. Cargala a mano si corresponde.' });
      }
    } catch {
      setMsg({ tipo: 'info', texto: 'No pudimos consultar el padrón. Cargala a mano.' });
    } finally { setBuscando(false); }
  };

  return (
    <div className="col-span-2">
      <button
        type="button"
        onClick={buscar}
        disabled={!dniOk || buscando}
        className="w-full py-2.5 rounded-xl border border-dashed border-indigo-300 bg-indigo-50/60 text-indigo-700 text-xs font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 transition-colors"
      >
        {buscando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
        {buscando ? 'Consultando el padrón…' : 'Traer obra social por DNI'}
      </button>
      {!dniOk ? (
        <p className="mt-1 text-[10px] text-gray-400 text-center">
          Escribí el DNI para consultar el padrón
        </p>
      ) : null}
      {msg && (
        <p className={`mt-2 text-[11px] flex items-start gap-1.5 rounded-lg px-2.5 py-1.5 ${
          msg.tipo === 'ok'
            ? 'text-emerald-700 bg-emerald-50 border border-emerald-200'
            : 'text-gray-600 bg-gray-50 border border-gray-200'
        }`}>
          {msg.tipo === 'ok' ? <Check className="w-3 h-3 mt-0.5 flex-shrink-0" />
                             : <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />}
          {msg.texto}
        </p>
      )}
    </div>
  );
};

// =====================================================================
// Modal de alta / edición de ficha
// =====================================================================
const CAMPOS: Array<[string, string, string?]> = [
  ['apellido', 'Apellido *'], ['nombre', 'Nombre *'],
  ['nro_doc', 'Número de documento'], ['fecha_nacimiento', 'Fecha de nacimiento', 'date'],
  ['telefono', 'Teléfono'], ['email', 'Email', 'email'],
  ['obra_social_nombre', 'Obra social'], ['nro_afiliado', 'N° de afiliado'],
];

const FichaModal: React.FC<{
  paciente: Paciente | null; base: string; auth: any;
  onClose: () => void; onGuardado: () => void;
}> = ({ paciente, base, auth, onClose, onGuardado }) => {
  const [form, setForm] = useState<any>(() => ({
    tipo_doc: 'DNI', sexo: '', ...(paciente || {}),
    fecha_nacimiento: paciente?.fecha_nacimiento ? String(paciente.fecha_nacimiento).substring(0, 10) : '',
  }));
  const [guardando, setGuardando] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const guardar = async () => {
    setGuardando(true); setErr(null);
    try {
      const esNuevo = !paciente;
      const res = await fetch(esNuevo ? base : `${base}/${paciente!.id}`, {
        method: esNuevo ? 'POST' : 'PUT',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const j = await res.json();
      if (j.success) onGuardado();
      else setErr(j.error || 'No se pudo guardar');
    } catch { setErr('Error de conexión'); }
    finally { setGuardando(false); }
  };

  return (
    <Hoja titulo={paciente ? 'Editar ficha' : 'Nuevo paciente'} onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        {CAMPOS.map(([campo, label, tipo]) => (
          <div key={campo} className={campo === 'email' || campo === 'obra_social_nombre' ? 'col-span-2' : ''}>
            <label className="block text-[11px] font-medium text-gray-500 mb-1">{label}</label>
            <input
              type={tipo || 'text'}
              value={form[campo] ?? ''}
              onChange={(e) => setForm({ ...form, [campo]: e.target.value })}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        ))}
        <div>
          <label className="block text-[11px] font-medium text-gray-500 mb-1">Sexo</label>
          <select
            value={form.sexo ?? ''}
            onChange={(e) => setForm({ ...form, sexo: e.target.value })}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">Sin especificar</option>
            <option value="F">Femenino</option>
            <option value="M">Masculino</option>
            <option value="X">X</option>
          </select>
        </div>
        <BotonCobertura form={form} setForm={setForm} auth={auth} />
      </div>

      {err && <p className="mt-3 text-xs text-red-600">{err}</p>}

      <button
        onClick={guardar}
        disabled={guardando}
        className="mt-5 w-full py-3.5 rounded-2xl bg-indigo-600 text-white text-sm font-semibold disabled:opacity-60 active:scale-[.98] transition-transform flex items-center justify-center gap-2"
      >
        {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        {paciente ? 'Guardar cambios' : 'Crear paciente'}
      </button>
    </Hoja>
  );
};

// =====================================================================
// Modal de conversión: confirmar el corte de nombre y apellido
// =====================================================================
const ConversionModal: React.FC<{
  contacto: Contacto; base: string; auth: any;
  onClose: () => void; onConvertido: () => void;
}> = ({ contacto, base, auth, onClose, onConvertido }) => {
  const [cargando, setCargando] = useState(true);
  const [form, setForm] = useState<any>({});
  const [turnos, setTurnos] = useState(0);
  const [guardando, setGuardando] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${base}/from-appointment/${contacto.turno_id}`, { headers: auth });
        const j = await r.json();
        if (j.success) {
          setForm({
            ...j.sugerencia,
            fecha_nacimiento: j.sugerencia.fecha_nacimiento
              ? String(j.sugerencia.fecha_nacimiento).substring(0, 10) : '',
          });
          setTurnos(j.turnosQueSeVincularian || 0);
        } else setErr(j.error || 'No se pudo preparar la conversión');
      } catch { setErr('Error de conexión'); }
      finally { setCargando(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const convertir = async () => {
    setGuardando(true); setErr(null);
    try {
      const r = await fetch(`${base}/from-appointment/${contacto.turno_id}`, {
        method: 'POST',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const j = await r.json();
      if (j.success) onConvertido();
      else setErr(j.error || 'No se pudo convertir');
    } catch { setErr('Error de conexión'); }
    finally { setGuardando(false); }
  };

  return (
    <Hoja titulo="Convertir en paciente" onClose={onClose}>
      {cargando ? (
        <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-indigo-500 animate-spin" /></div>
      ) : (
        <>
          <p className="text-xs text-gray-500 mb-4">
            El turno figura a nombre de <strong className="text-gray-700">{contacto.client_name}</strong>.
            Revisá cómo queda partido — el apellido va en la receta, así que conviene que esté exacto.
          </p>

          <div className="grid grid-cols-2 gap-3">
            {[['apellido', 'Apellido *'], ['nombre', 'Nombre *'],
              ['nro_doc', 'Documento'], ['fecha_nacimiento', 'Nacimiento', 'date'],
              ['telefono', 'Teléfono'], ['email', 'Email'],
              ['obra_social_nombre', 'Obra social'], ['nro_afiliado', 'N° afiliado'],
            ].map(([campo, label, tipo]) => (
              <div key={campo}>
                <label className="block text-[11px] font-medium text-gray-500 mb-1">{label}</label>
                <input
                  type={(tipo as string) || 'text'}
                  value={form[campo as string] ?? ''}
                  onChange={(e) => setForm({ ...form, [campo as string]: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            ))}
            <div>
              <label className="block text-[11px] font-medium text-gray-500 mb-1">Sexo</label>
              <select
                value={form.sexo ?? ''}
                onChange={(e) => setForm({ ...form, sexo: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Sin especificar</option>
                <option value="F">Femenino</option>
                <option value="M">Masculino</option>
                <option value="X">X</option>
              </select>
            </div>
            <BotonCobertura form={form} setForm={setForm} auth={auth} />
          </div>

          {turnos > 1 && (
            <p className="mt-3 text-[11px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-xl px-3 py-2">
              Se van a vincular <strong>{turnos} turnos</strong> de esta persona a la ficha nueva.
            </p>
          )}

          {err && <p className="mt-3 text-xs text-red-600">{err}</p>}

          <button
            onClick={convertir}
            disabled={guardando}
            className="mt-5 w-full py-3.5 rounded-2xl bg-emerald-600 text-white text-sm font-semibold disabled:opacity-60 active:scale-[.98] transition-transform flex items-center justify-center gap-2"
          >
            {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            Crear ficha
          </button>
        </>
      )}
    </Hoja>
  );
};

// =====================================================================
// Hoja contenedora (sube desde abajo en celular, centrada en escritorio)
// =====================================================================
// ===================================================================
// Historial del paciente: turnos, recetas e historias en un solo lugar
// ===================================================================
interface Historial {
  turnos: Array<{ id: number; appointment_date: string; appointment_time: string | null; status: string | null; price: string | number | null }>;
  recetas: Array<{
    id: number; cuir: string; status: string; issued_at: string; expires_at: string;
    diagnosis_description: string | null; qbi2_estado: string | null;
    qbi2_id_receta: string | null; qbi2_cuir: string | null; qbi2_s3_link: string | null;
  }>;
  historias: Array<{ id: number; record_date: string | null; status: string | null; created_at: string; completed_at: string | null }>;
}

const HistorialPaciente: React.FC<{
  paciente: Paciente;
  base: string;
  auth: Record<string, string>;
  onClose: () => void;
  onEditar: () => void;
}> = ({ paciente, base, auth, onClose, onEditar }) => {
  const [datos, setDatos] = useState<Historial | null>(null);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const r = await fetch(`${base}/${paciente.id}/historial`, { headers: auth });
        const j = await r.json();
        if (!vivo) return;
        if (!r.ok || j.success === false) setFallo(j.error || 'No pudimos cargar el historial');
        else setDatos({ turnos: j.turnos || [], recetas: j.recetas || [], historias: j.historias || [] });
      } catch {
        if (vivo) setFallo('No pudimos conectarnos.');
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => { vivo = false; };
  }, [base, paciente.id, auth]);

  const dia = (iso?: string | null) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return '—'; }
  };

  const Seccion: React.FC<{ icono: React.ReactNode; titulo: string; cantidad: number; children: React.ReactNode }> = ({
    icono, titulo, cantidad, children,
  }) => (
    <div className="mt-5">
      <div className="flex items-center gap-2 mb-2 px-1">
        {icono}
        <h4 className="text-[13px] font-semibold text-gray-700 uppercase tracking-wide">{titulo}</h4>
        <span className="text-[12px] text-gray-500">({cantidad})</span>
      </div>
      {cantidad === 0
        ? <p className="text-[13px] text-gray-500 px-1">Todavía no hay nada.</p>
        : <div className="bg-gray-50 rounded-xl divide-y divide-gray-200/70 overflow-hidden">{children}</div>}
    </div>
  );

  return (
    <Hoja titulo={`${paciente.apellido}, ${paciente.nombre}`} onClose={onClose}>
      <div className="text-left">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-gray-700">
          {paciente.nro_doc && <span>{paciente.tipo_doc} {paciente.nro_doc}</span>}
          {paciente.telefono && <span>· {paciente.telefono}</span>}
          {paciente.obra_social_nombre && (
            <span className="text-teal-700">
              · {paciente.obra_social_nombre}{paciente.nro_afiliado ? ` ${paciente.nro_afiliado}` : ''}
            </span>
          )}
          <button onClick={onEditar} className="ml-auto text-[13px] font-semibold text-indigo-600 hover:underline">
            Editar datos
          </button>
        </div>

        {cargando && (
          <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>
        )}

        {!cargando && fallo && (
          <p className="mt-4 text-[13px] text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{fallo}</p>
        )}

        {!cargando && datos && (
          <>
            <Seccion icono={<FileSignature className="w-4 h-4 text-gray-600" />} titulo="Recetas" cantidad={datos.recetas.length}>
              {datos.recetas.map((r) => (
                <div key={r.id} className="px-3 py-2.5 flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-[13px] text-gray-900 truncate">
                      {r.qbi2_cuir || r.qbi2_id_receta || r.cuir}
                    </p>
                    <p className="text-[12px] text-gray-600">
                      {dia(r.issued_at)}
                      {r.diagnosis_description ? ` · ${r.diagnosis_description}` : ''}
                      {r.status === 'cancelled' ? ' · cancelada' : ''}
                    </p>
                  </div>
                  {/* Solo las que llegaron al repositorio tienen PDF oficial. */}
                  {r.qbi2_s3_link && (
                    <a
                      href={r.qbi2_s3_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-[12px] font-semibold text-indigo-600 hover:underline flex-shrink-0"
                    >
                      <FileText className="w-3.5 h-3.5" /> Ver
                    </a>
                  )}
                </div>
              ))}
            </Seccion>

            <Seccion icono={<CalendarDays className="w-4 h-4 text-gray-600" />} titulo="Turnos" cantidad={datos.turnos.length}>
              {datos.turnos.map((t) => (
                <div key={t.id} className="px-3 py-2.5 flex items-center justify-between gap-3">
                  <span className="text-[13px] text-gray-900">
                    {dia(t.appointment_date)}{t.appointment_time ? ` · ${String(t.appointment_time).slice(0, 5)}` : ''}
                  </span>
                  {t.status && <span className="text-[12px] text-gray-600">{t.status}</span>}
                </div>
              ))}
            </Seccion>

            <Seccion icono={<ClipboardList className="w-4 h-4 text-gray-600" />} titulo="Historias clínicas" cantidad={datos.historias.length}>
              {datos.historias.map((h) => (
                <div key={h.id} className="px-3 py-2.5 flex items-center justify-between gap-3">
                  <span className="text-[13px] text-gray-900">{dia(h.record_date || h.created_at)}</span>
                  {h.status && <span className="text-[12px] text-gray-600">{h.status}</span>}
                </div>
              ))}
            </Seccion>
          </>
        )}
      </div>
    </Hoja>
  );
};

const Hoja: React.FC<{ titulo: string; onClose: () => void; children: React.ReactNode }> = ({
  titulo, onClose, children,
}) => (
  <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center">
    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
    <div className="relative w-full sm:max-w-lg max-h-[92vh] overflow-y-auto bg-white rounded-t-[28px] sm:rounded-[28px] shadow-2xl">
      <div className="sm:hidden pt-3 pb-1 flex justify-center">
        <div className="w-9 h-1 rounded-full bg-gray-300" />
      </div>
      <div className="px-6 pt-4 pb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{titulo}</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        {children}
      </div>
    </div>
  </div>
);
